import { authenticatedApiRequest } from "./core";
import type { PaginatedResponse } from "./core";
import type {
  RevenueStatement,
  ContributorsRevenueItem,
  TopTrack,
  TrackRevenue,
  RoyaltyStatement,
  RoyaltyStatementBulkCreatePayload,
  BulkSettlePayload,
  BulkSettlePreviewPayload,
  BulkSettlePreviewResponse,
  RoyaltyStatementPreviewChecks,
} from "@/types";

export interface RoyaltyStatementQueryParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
  status?: string;
  payees?: string;
  contract?: string;
  currency?: string;
  period_start_after?: string;
  period_end_before?: string;
}

export interface PaginatedRoyaltyStatementsResponse extends PaginatedResponse {
  results: RoyaltyStatement[];
}

export interface RevenueStatementQueryParams {
  label_id?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  q?: string;
  is_settled?: string;
  vendor?: string;
}

export interface PaginatedRevenueStatementsResponse extends PaginatedResponse {
  results: RevenueStatement[];
}

export interface PaginatedContributorsRevenueResponse extends PaginatedResponse {
  results: ContributorsRevenueItem[];
}

export interface PaginatedTrackRevenueResponse extends PaginatedResponse {
  results: TrackRevenue[];
}

export interface PaginatedTopTrackResponse extends PaginatedResponse {
  results: TopTrack[];
}

export interface StatementContributorsRevenueQueryParams {
  ordering?: string;
  page?: number;
  page_size?: number;
  q?: string;
}

export interface StatementTopTracksQueryParams {
  ordering?: string;
  page?: number;
  page_size?: number;
  q?: string;
}

export interface StatementTrackRevenueQueryParams {
  ordering?: string;
  page?: number;
  page_size?: number;
  q?: string;
}

export interface RoyaltyStatementHtmlResponse {
  // null: finalized statement without a stored HTML snapshot (predates the
  // snapshot field) — fall back to the statement's PDF file.
  html: string | null;
}

export const revenueApi = {
  getRevenueStatement: async (id: string): Promise<RevenueStatement> => {
    return authenticatedApiRequest<RevenueStatement>(`/revenue/statements/${id}`);
  },
  getRevenueStatements: async (
    filters?: RevenueStatementQueryParams,
  ): Promise<PaginatedRevenueStatementsResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedRevenueStatementsResponse>(
      `/revenue/statements${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  getStatementContributorsRevenue: async (
    statementId: string,
    filters?: StatementContributorsRevenueQueryParams,
  ): Promise<PaginatedContributorsRevenueResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedContributorsRevenueResponse>(
      `/revenue/statements/${statementId}/contributors-revenue${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  getStatementTopTracks: async (
    statementId: string,
    filters?: StatementTopTracksQueryParams,
  ): Promise<PaginatedTopTrackResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedTopTrackResponse>(
      `/revenue/statements/${statementId}/top-tracks${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  getStatementTrackRevenue: async (
    statementId: string,
    filters?: StatementTrackRevenueQueryParams,
  ): Promise<PaginatedTrackRevenueResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedTrackRevenueResponse>(
      `/revenue/statements/${statementId}/track-revenue${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  bulkCreateRoyaltyStatements: async (
    payload: RoyaltyStatementBulkCreatePayload,
  ): Promise<RoyaltyStatement[]> => {
    return authenticatedApiRequest<RoyaltyStatement[]>("/revenue/royalty-statements/bulk-create", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  getRoyaltyStatements: async (
    params?: RoyaltyStatementQueryParams,
  ): Promise<PaginatedRoyaltyStatementsResponse> => {
    const queryParams = params
      ? new URLSearchParams(
          Object.entries(params).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedRoyaltyStatementsResponse>(
      `/revenue/royalty-statements${queryParams ? `?${queryParams}` : ""}`,
    );
  },
  getRoyaltyStatement: async (id: string): Promise<RoyaltyStatement> => {
    return authenticatedApiRequest<RoyaltyStatement>(`/revenue/royalty-statements/${id}`);
  },
  // Confirm cash received from the distributor. The settlement fields
  // (amount_received, date_received, exchange_rate, settlement_difference_reason)
  // are read-only on the statement detail endpoint — this action is the only way
  // to write them, and it books the matching ledger entries.
  settleRevenueStatement: async (
    id: string,
    payload: { payment_date: string; amount_received: string; reason?: string },
  ): Promise<RevenueStatement> => {
    return authenticatedApiRequest<RevenueStatement>(`/revenue/statements/${id}/settle`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  // Per-statement split of one distributor payment, for the user to confirm.
  // Informational only — the confirm endpoint recomputes the split server-side
  // and requires this response's fingerprint, so it books exactly these
  // numbers or rejects (statement rebuilt / amounts changed in between).
  previewBulkSettleRevenueStatements: async (
    payload: BulkSettlePreviewPayload,
  ): Promise<BulkSettlePreviewResponse> => {
    return authenticatedApiRequest<BulkSettlePreviewResponse>(
      "/revenue/statements/bulk-settle/preview",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
  // Settle a group of statements against one distributor payment. The group
  // must share one vendor and one currency; refund (negative) statements are
  // netted at their booked value. Returns the settled statements.
  bulkSettleRevenueStatements: async (payload: BulkSettlePayload): Promise<RevenueStatement[]> => {
    return authenticatedApiRequest<RevenueStatement[]>("/revenue/statements/bulk-settle", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  // Edits the statement dates; the backend rebuilds derived data from the
  // import batch (statement flips to "generating" until the rebuild lands).
  // All three fields must be sent together — the API rejects a partial trio.
  // Blocked with a 400 once associated journal entries are posted, and with a
  // 409 while a rebuild is already in flight.
  patchRevenueStatement: async (
    id: string,
    payload: { period_start: string; period_end: string; statement_received_date: string },
  ): Promise<RevenueStatement> => {
    return authenticatedApiRequest<RevenueStatement>(`/revenue/statements/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  deleteRevenueStatement: async (id: string): Promise<void> => {
    return authenticatedApiRequest<void>(`/revenue/statements/${id}`, {
      method: "DELETE",
    });
  },
  deleteRoyaltyStatement: async (id: string): Promise<void> => {
    return authenticatedApiRequest<void>(`/revenue/royalty-statements/${id}`, {
      method: "DELETE",
    });
  },
  markRoyaltyStatementSent: async (id: string): Promise<RoyaltyStatement> => {
    return authenticatedApiRequest<RoyaltyStatement>(`/revenue/royalty-statements/${id}/mark-sent`, {
      method: "POST",
    });
  },
  markRoyaltyStatementPaid: async (id: string): Promise<RoyaltyStatement> => {
    return authenticatedApiRequest<RoyaltyStatement>(`/revenue/royalty-statements/${id}/mark-paid`, {
      method: "POST",
    });
  },
  getRoyaltyStatementHtml: async (id: string): Promise<RoyaltyStatementHtmlResponse> => {
    return authenticatedApiRequest<RoyaltyStatementHtmlResponse>(
      `/revenue/royalty-statements/${id}/html`,
    );
  },
  // Pre-creation checks for a contract + period: overlapping revenue
  // statements and whether the period has any revenue/expense activity.
  getRoyaltyStatementPreviewChecks: async (params: {
    contract_id: string;
    period_start: string;
    period_end: string;
  }): Promise<RoyaltyStatementPreviewChecks> => {
    const query = new URLSearchParams(params).toString();
    return authenticatedApiRequest<RoyaltyStatementPreviewChecks>(
      `/revenue/royalty-statements/preview/checks?${query}`,
    );
  },
  getRoyaltyStatementPreviewHtml: async (params: {
    contract_id: string;
    payee_id: string;
    period_start: string;
    period_end: string;
  }): Promise<RoyaltyStatementHtmlResponse> => {
    const query = new URLSearchParams(params).toString();
    return authenticatedApiRequest<RoyaltyStatementHtmlResponse>(
      `/revenue/royalty-statements/preview/html?${query}`,
    );
  },
};
